import { computed } from 'vue';
import { useWarehouseStore } from '@/store/useWarehouseStore';
import { useYardTaskPresenter } from '@satellite/composables/useYardTaskPresenter';
import type { TaskViewData } from '@nova/core';
import type { ComputedRef, Ref } from 'vue';

export type UseYardTaskParams = {
  // initialTask is used only for initial state
  initialTask?:
    | TaskViewData
    | Ref<TaskViewData | null | undefined>
    | ComputedRef<TaskViewData | null | undefined>
    | null;
};

export function useYardTask({ initialTask }: UseYardTaskParams = {}) {
  const warehouseStore = useWarehouseStore();

  /**
   * Type assertion needed due to monorepo module resolution issue. Ex.:
   * - apps/circe imports Vue types from apps/circe/node_modules/@vue/reactivity/...
   * - libs/satellite imports Vue types from libs/satellite/node_modules/@vue/reactivity/...
   * TypeScript treats these as different types even though they're structurally identical.
   * The types are compatible at runtime, but TypeScript sees them as incompatible due to different module paths.
   * Using Parameters<typeof useYardTaskPresenter>[0] extracts the exact expected type to bypass this issue.
   */
  const presenter = useYardTaskPresenter(
    initialTask as Parameters<typeof useYardTaskPresenter>[0],
    {
      warehouseTimezone: warehouseStore.selectedWarehouseTimezone ?? undefined
    }
  );

  // Add fallback for task description (Circe-specific)
  const taskDescription = computed(() => {
    return presenter.taskDescription.value || 'No description';
  });

  return {
    // All presenter properties (including events from ViewData)
    ...presenter,

    // Override taskDescription with Circe-specific fallback
    taskDescription
  };
}
